import type { AnalyticsEvent } from '$lib/services/tracking/googleAnalytics/events';
import type { DataLayerObject, Mouseflow } from '$lib/services/tracking/googleAnalytics/type';
import { browser } from '$app/environment';
import { page } from '$app/stores';
import { handleVendorConsent } from '$lib/services/tracking/didomi/consent';
import { DIDOMI_VENDOR_APP_ID, MOUSEFLOW_ALLOWED_LOCALES, MOUSEFLOW_WEBSITE_ID } from '$lib/services/tracking/mouseflow/constant';
import { useSplunkRum } from '$lib/services/tracking/splunk/splunkRum';
import { isHomePage } from '$lib/services/urls/routeIds';
import { getCountryFromLocale } from '@viamichelin/business';
import { onMount } from 'svelte';
import { get } from 'svelte/store';

// SDK API: https://js-api-docs.mouseflow.com/
// Record documentation: https://help.mouseflow.com/en/articles/4310694-start-recording-based-on-user-action
// VM Mouseflow integration docs: https://www.notion.so/Comment-tester-nos-int-grations-de-librairies-tierces-122bd4bbe4f18018b018cf0f45ecf2b7?pvs=4#122bd4bbe4f18078af09f8bc55a6b26a

function pageHasMouseflowAllowedLocale() {
	const $page = get(page);
	return MOUSEFLOW_ALLOWED_LOCALES.includes($page.data.locale);
}

function mouseflowMustStartRecording() {
	return isHomePage(new URL(window.location.href)) && pageHasMouseflowAllowedLocale();
}

function mouseflowMustContinueRecording() {
	return !!sessionStorage.getItem('mf_record_user') && pageHasMouseflowAllowedLocale();
}

interface AppMouseflow {
	manualStart: () => void;
	manualStop: () => void;
	removeEventListeners: () => void;
}

function withMouseflow(callback: (mouseflow: Mouseflow) => any) {
	if (browser && window.mouseflow) {
		return callback(window.mouseflow);
	}
}

export const mouseflowIntegration = {
	start() {
		withMouseflow((mouseflow) => {
			const eventPageView = getLastPageViewFromDataLayer();
			const pagename = eventPageView && eventPageView[2]?.page_name;

			if (pagename) {
				mouseflowIntegration.newPageView(pagename);
			}

			mouseflow.start();

			if (mouseflow.isRecording()) {
				useSplunkRum((SplunkRum) => {
					SplunkRum.setGlobalAttributes({
						mouseflow_recording: true,
					});
				});
			}
		});
	},
	newPageView(pagename: string) {
		withMouseflow((mouseflow) => {
			const { locale } = get(page).data;
			const countryCode = getCountryFromLocale(locale);
			const pageViewName = `${pagename} ${countryCode}`;

			mouseflow.newPageView(pageViewName);
		});
	},
	tag(tagName: string) {
		withMouseflow((mouseflow) => {
			mouseflow.tag(tagName);
		});
	},
	stop() {
		withMouseflow((mouseflow) => {
			mouseflow.stop();
		});
	},
	isRecording(): boolean {
		return withMouseflow((mouseflow) => {
			mouseflow.isRecording();
		});
	},
};

export function useMouseflow() {
	// Initialize on mount to record only from first page load
	onMount(() => {
		let appMouseflow: AppMouseflow | undefined;

		const mustStartRecording = mouseflowMustStartRecording();
		const mustContinueRecording = mouseflowMustContinueRecording();
		if (mustStartRecording || mustContinueRecording) {
			initializeMouseflowOnVendorConsent((newAppMouseflow) => {
				appMouseflow = newAppMouseflow;
				if (mustStartRecording && !mustContinueRecording) {
					appMouseflow.manualStart();
				}
			});
		}

		return () => {
			appMouseflow?.removeEventListeners();
		};
	});
}

async function initializeMouseflowOnVendorConsent(
	onMouseflowReady: (appMouseflow: AppMouseflow) => void,
) {
	handleVendorConsent(DIDOMI_VENDOR_APP_ID, async () => {
		await initializeMouseflow();

		function onVisibilityChange() {
			if (document.visibilityState === 'visible') {
				if (!mouseflowIntegration.isRecording() && mouseflowMustContinueRecording()) {
					mouseflowIntegration.start();
				}
			}
		}

		document.addEventListener('visibilitychange', onVisibilityChange);

		if (mouseflowMustContinueRecording()) {
			mouseflowIntegration.start();
		}

		onMouseflowReady({
			manualStart() {
				sessionStorage.setItem('mf_record_user', '1');
				mouseflowIntegration.start();
			},
			manualStop() {
				sessionStorage.removeItem('mf_record_user');
				mouseflowIntegration.stop();
			},
			removeEventListeners() {
				document.removeEventListener('visibilitychange', onVisibilityChange);
			},
		});
	});
}

function getLastPageViewFromDataLayer(): DataLayerObject | undefined {
	if (!browser || !window.dataLayer) {
		return;
	}
	const reversedDataLayer = [...window.dataLayer].reverse();
	const eventPageView = reversedDataLayer.find((eventArgs) => {
		if (eventArgs[0] === 'event' && eventArgs[1] === 'page_view') {
			return true;
		}
		return false;
	});

	return eventPageView;
}

async function initializeMouseflow() {
	if (!browser) {
		return;
	}
	return new Promise<Mouseflow>((resolve) => {
		// Prevent Mouseflow from starting automatically
		window.mouseflowAutoStart = false;

		// Install Mouseflow script - start (https://help.mouseflow.com/en/articles/4264201-how-to-install-mouseflow)
		window._mfq = window._mfq || [];
		const mf = document.createElement('script');
		mf.id = 'mouseflow-script';
		mf.type = 'text/javascript';
		mf.defer = true;
		mf.src = `//cdn.mouseflow.com/projects/${MOUSEFLOW_WEBSITE_ID}.js`;
		mf.onload = () => {
			if (window.mouseflow) {
				resolve(window.mouseflow);
			}
		};
		document.getElementsByTagName('head')[0].appendChild(mf);
		// Install Mouseflow script - end
	});
}

export function mouseflowOnAnalyticsEvent(event: AnalyticsEvent, payload: any) {
	if (event.action === 'page_view') {
		return;
	}

	if (event.action === 'add_interactions' && payload.action === 'click' && payload.add_type === 'boxmap' && payload.add_advertiser === 'BOOKING') {
		mouseflowIntegration.tag('boxmap_booking');
	}

	if (event.action === 'call_to_action' && payload.poi_category === 'accommodation' && payload.cta_type === 'datasheet_book') {
		mouseflowIntegration.tag('cta_hotel_ds');
	}

	if (event.action === 'car_fill_start') {
		mouseflowIntegration.tag('car_fill_start');
	}

	if (event.action === 'car_fill_finish') {
		mouseflowIntegration.tag('car_fill_finish');
	}

	if (event.action === 'itinerary_details') {
		mouseflowIntegration.tag('itinerary_details');
	}
}
